import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BackButton } from '../BackButton';
import { Button } from '../Button';
import { FAIcon } from '../fa-icon';
import { formatClassName } from '../../utils';
import { useContainerProps } from '../../hooks/useContainerProps';
import { renderContainerChildren } from '../../utils/RenderUtil';
import { reactifiPageTitle as setTitle } from '../../metatags/title';
import classNames from 'classnames';
import { Core } from '../..';
import { DesignSystem } from '@everfi/design-system';

/** renders a div that is styled to fill the header area */
export const Header = props => {
  props = useContainerProps(props);
  const {
    ariaLabel,
    back,
    button,
    className,
    data,
    headerTabs,
    icon,
    iconProps,
    noBorder,
    noPadding,
    noBackground,
    router,
    subTitleSection,
    title,
    WrapperComponent,
    additionalTitleDisplay,
    hasNotifications,
    isSticky = true,
  } = props;

  function renderTitleBar() {
    if (!back && !icon && !title && !button) {
      return null;
    }

    if (WrapperComponent) {
      return (
        <WrapperComponent>
          <div className="header-titlebar" style={{ padding: 0 }}>
            {renderTitleBarInner()}
          </div>
        </WrapperComponent>
      );
    } else {
      return (
        <div
          className={formatClassName(['header-titlebar', { 'no-background': noBackground, 'header-tabs': headerTabs }])}
        >
          {renderTitleBarInner()}
        </div>
      );
    }
  }

  function renderTitleBarInner() {
    return (
      <>
        <Core.Layout variant={'flex'} alignItems={'center'} gap={DesignSystem.Space['2XS']}>
          {back && <BackButton action={back} router={router} data-back-arrow={true} />}
          {icon && typeof icon === 'string' ? (
            <FAIcon icon={icon} {...iconProps} additionalClasses="header-titlebar-icon" />
          ) : (
            icon
          )}
          <h1 aria-label={ariaLabel || title} className="header-titlebar-title" {...getDataProps(data)}>
            {title}
          </h1>
          {additionalTitleDisplay && <Core.Layout>{additionalTitleDisplay}</Core.Layout>}
        </Core.Layout>
        <div className="header-titlebar-right">{renderButtons()}</div>
      </>
    );
  }

  function renderButtons() {
    if (Array.isArray(button)) {
      return button.map(renderButton);
    } else if (button && button.action) {
      return renderButton(button);
    } else {
      return button;
    }
  }

  /* eslint-disable-next-line react/no-multi-comp */
  function renderButton(button, index) {
    const { icon, rightIcon, label, action } = button;
    if (!icon && !label) {
      return null;
    }

    button = { ...button, className: classNames('ui-default', button?.className) };

    const hasMultipleActions = Array.isArray(action) && action.length > 1;
    const handler = Array.isArray(action) && action.length === 1 ? action[0].action : action;

    return hasMultipleActions ? (
      <Core.Layout data-object-id={'main-header-dropdown-action-menu'}>
        <Core.DropdownMenu
          variant="secondary"
          className={`ui-default btn btn-${button.style}`}
          disabled={button.disabled}
          menuButtonText={
            <Core.Layout {...getDataProps(button.data)} variant={'flex'} gap={DesignSystem.Space.XS}>
              {icon && <Core.Icon iconName={icon} additionalClasses="left" size={DesignSystem.Sizes.XS} />}
              {label}
            </Core.Layout>
          }
          items={button.action.map(({ label, action, data }) => ({
            text: <Core.Layout {...getDataProps(data)}>{label}</Core.Layout>,
            onClick: action,
          }))}
          placement="bottom-start"
          menuButtonRightIcon={
            rightIcon ? <Core.Icon iconName={rightIcon} additionalClasses="right" size={DesignSystem.Sizes.XS} /> : null
          }
        />
      </Core.Layout>
    ) : (
      <Button key={index} {...button} onClick={handler} />
    );
  }

  function getDataProps(data = {}) {
    const dataProps = {};
    if (data.action) {
      dataProps['data-action'] = data.action;
    }
    if (data.objectId) {
      dataProps['data-object-id'] = data.objectId;
    }
    if (data.objectType) {
      dataProps['data-object-type'] = data.objectType;
    }
    return dataProps;
  }

  useEffect(() => {
    if (title) {
      setTitle(title);
    }
  }, [title]);

  return (
    <div
      className={formatClassName([
        'header',
        className,
        { 'no-border': noBorder, 'no-padding': noPadding, 'no-background': noBackground, 'header-sticky': isSticky },
      ])}
    >
      {renderTitleBar()}
      {subTitleSection && subTitleSection}
      {hasNotifications && headerTabs}
      {renderContainerChildren(props, null, WrapperComponent)}
      {!hasNotifications && headerTabs}
    </div>
  );
};

Header.propTypes = {
  additionalTitleDisplay: PropTypes.node,
  ariaLabel: PropTypes.string,
  back: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  button: PropTypes.oneOfType([
    PropTypes.shape(Button.propTypes),
    PropTypes.arrayOf(PropTypes.shape(Button.propTypes)),
  ]),
  children: PropTypes.node,
  className: PropTypes.string,
  data: PropTypes.object,
  subTitleSection: PropTypes.node,
  headerTabs: PropTypes.node,
  icon: PropTypes.string,
  iconProps: PropTypes.object,
  noBackground: PropTypes.bool,
  noBorder: PropTypes.bool,
  noPadding: PropTypes.bool,
  router: PropTypes.shape({ goBack: PropTypes.func }),
  title: PropTypes.string,
  WrapperComponent: PropTypes.elementType,
  hasNotifications: PropTypes.bool,
  isSticky: PropTypes.bool,
};
